import { useEffect } from 'react';
import type React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { StatusPage } from './StatusPage';
import { AppShell } from './AppShell';
import { unlockSounds } from './sounds';
import { ErrorBoundary } from './ErrorBoundary';
import { RealtimeProvider } from './realtime';
import { CompaniesPage } from '../crm/CompaniesPage';
import { BoardPage } from '../crm/BoardPage';
import { ContactsPage, OpportunitiesPage } from '../crm/EntityListPage';
import { LeadsPage } from '../crm/LeadsPage';
import { ImportPage } from '../crm/ImportPage';
import { InboxPage } from '../inbox/InboxPage';
import { ChannelsPage } from '../inbox/ChannelsPage';
import { QueuesPage } from '../inbox/QueuesPage';
import { TicketsPage } from '../support/TicketsPage';
import { KnowledgePage } from '../ai/KnowledgePage';
import { BatchesPage } from '../ai/BatchesPage';
import { ProposalsPage } from '../sales/ProposalsPage';
import { ProposalPage } from '../sales/ProposalPage';
import { ContractsPage } from '../sales/ContractsPage';
import { ProposalAcceptPage } from '../sales/ProposalAcceptPage';
import { PortalPage } from '../support/PortalPage';
import { TicketPage } from '../support/TicketPage';
import { AccountsPage } from '../success/AccountsPage';
import { AccountPage as CustomerAccountPage } from '../success/AccountPage';
import { TemplatesPage as OnboardingTemplatesPage } from '../success/TemplatesPage';
import { PlaybooksPage } from '../success/PlaybooksPage';
import { TemplatesPage } from '../inbox/TemplatesPage';
import { ProjectPage } from '../work/ProjectPage';
import { ProjectsPage } from '../work/ProjectsPage';
import { TasksPage } from '../work/TasksPage';
import { TaskPage } from '../work/TaskPage';
import { BacklogPage } from '../work/BacklogPage';
import { AutomationsPage } from '../automations/AutomationsPage';
import { AutomationBuilder } from '../automations/AutomationBuilder';
import { FilesPage } from '../files/FilesPage';
import { DomainsPage } from '../marketing/DomainsPage';
import { FormsPage } from '../marketing/FormsPage';
import { PagesPage } from '../marketing/PagesPage';
import { PageBuilderPage } from '../marketing/PageBuilderPage';
import { EmailPage } from '../marketing/EmailPage';
import { AgentsPage } from '../ai/AgentsPage';
import { DashboardPage } from '../dashboard/DashboardPage';
import { CataloguesPage } from '../settings/CataloguesPage';
import { PreferencesPage } from '../settings/PreferencesPage';
import { FormBuilderPage } from '../marketing/FormBuilderPage';
import { DealPage } from '../crm/DealPage';
import { CampaignsPage } from '../marketing/CampaignsPage';
import { CampaignWizard } from '../marketing/CampaignWizard';
import { SocialPage } from '../social/SocialPage';
import { AdsPage } from '../ads/AdsPage';
import { UsagePage } from '../settings/UsagePage';
import { IntegrationsPage } from '../settings/IntegrationsPage';
import { ForecastPage } from '../crm/ForecastPage';
import { KeyDatesPage } from '../crm/KeyDatesPage';
import { CadencesPage } from '../crm/CadencesPage';
import { OrdersPage } from '../crm/OrdersPage';
import { SharePage } from '../files/SharePage';
import { SmsPage } from '../sms/SmsPage';
import { WhatsAppCampaignsPage } from '../whatsapp/CampaignsPage';
import { AccountPage } from '../settings/AccountPage';
import { CompanyPage } from '../settings/CompanyPage';
import { TeamPage } from '../settings/TeamPage';
import { SsoPage } from '../settings/SsoPage';
import { PrivacyPage } from '../settings/PrivacyPage';
import { AuditPage } from '../settings/AuditPage';
import { BillingPage } from '../settings/BillingPage';
import { AdminPage } from '../admin/AdminPage';
import { TenantPage } from '../admin/TenantPage';
import { CalendarPage } from '../calendar/CalendarPage';
import { ChatPage } from '../chat/ChatPage';
import { RecordPage } from '../crm/RecordPage';
import { SearchPage } from './SearchPage';
import { LoginPage } from '../auth/LoginPage';
import { SignupPage } from '../auth/SignupPage';
import { MagicLinkPage } from '../auth/MagicLinkPage';
import { SsoStartPage } from '../auth/SsoStartPage';
import { ForgotPasswordPage } from '../auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../auth/ResetPasswordPage';
import { VerifyEmailPage } from '../auth/VerifyEmailPage';
import { InvitePage } from '../auth/InvitePage';
import { AuthProvider } from '../auth/auth-context';
import { useAuth } from '../auth/auth-store';
import { PreferencesProvider } from './preferences';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10_000 },
  },
});

/** Routes that require a session; anonymous visitors are sent to the login screen. */
function RequireAuth({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { status } = useAuth();

  if (status === 'loading') return <main className="page" aria-busy="true" />;
  if (status === 'anonymous') return <Navigate to="/entrar" replace />;

  return <AppShell>{children}</AppShell>;
}

/** Only Safe Cortex staff. The server enforces this; the guard keeps the URL from being a dead end. */
function RequirePlatformAdmin({ children }: { children: React.JSX.Element }): React.JSX.Element {
  const { user, status } = useAuth();

  if (status === 'loading') return <main className="page" aria-busy="true" />;
  if (status === 'anonymous') return <Navigate to="/entrar" replace />;
  if (!user?.is_platform_admin) return <Navigate to="/" replace />;

  return <AppShell>{children}</AppShell>;
}

/**
 * The route table.
 *
 * Every path here is in the rail and every rail entry is here — the navigation module is the one
 * list, so a screen cannot exist with no way to reach it, and a menu entry cannot point at nothing.
 * Areas whose API exists but whose interface does not say so plainly rather than opening a blank
 * page (context.md section 194).
 */
function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/entrar" element={<LoginPage />} />
      <Route path="/cadastro" element={<SignupPage />} />
      {/* Where the e-mail sign-in link lands; the token never leaves this route. */}
      <Route path="/entrar/link" element={<MagicLinkPage />} />
      {/* A company's stable sign-in link; it only asks the API where to send the browser. */}
      <Route path="/entrar/sso/:connectionId" element={<SsoStartPage />} />
      {/* Where the confirmation e-mail lands. The API's `verificationUrl` builds this path. */}
      <Route path="/verificar-email" element={<VerifyEmailPage />} />
      <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      {/* Where a team invitation lands; it ends in a session, not at the sign-in screen. */}
      <Route path="/convite" element={<InvitePage />} />
      <Route path="/status" element={<StatusPage />} />
      {/* The customer's page: a link is the whole credential (ADR 0012). */}
      <Route path="/proposta/:token" element={<ProposalAcceptPage />} />
      <Route path="/chamados/:token" element={<PortalPage />} />
      <Route path="/compartilhado/:token" element={<SharePage />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />

      {/* ----- CRM ----- */}
      <Route
        path="/crm/importar"
        element={
          <RequireAuth>
            <ImportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/leads"
        element={
          <RequireAuth>
            <LeadsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/contatos"
        element={
          <RequireAuth>
            <ContactsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/empresas"
        element={
          <RequireAuth>
            <CompaniesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/oportunidades"
        element={
          <RequireAuth>
            <OpportunitiesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/funil"
        element={
          <RequireAuth>
            <BoardPage />
          </RequireAuth>
        }
      />
      {/* One screen for every entity: the fields differ, the timeline does not. */}
      <Route
        path="/crm/negocios/:id"
        element={
          <RequireAuth>
            <DealPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/registro/:entity/:id"
        element={
          <RequireAuth>
            <RecordPage />
          </RequireAuth>
        }
      />
      {/* The old flat paths still work: a bookmark should not break on a reorganisation. */}
      <Route path="/empresas" element={<Navigate to="/crm/empresas" replace />} />

      {/* ----- Atendimento ----- */}
      <Route
        path="/atendimento"
        element={
          <RequireAuth>
            <InboxPage />
          </RequireAuth>
        }
      />
      <Route
        path="/atendimento/canais"
        element={
          <RequireAuth>
            <ChannelsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/atendimento/filas"
        element={
          <RequireAuth>
            <QueuesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/atendimento/chamados"
        element={
          <RequireAuth>
            <TicketsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/atendimento/chamados/:ticketId"
        element={
          <RequireAuth>
            <TicketPage />
          </RequireAuth>
        }
      />
      <Route
        path="/atendimento/templates"
        element={
          <RequireAuth>
            <TemplatesPage />
          </RequireAuth>
        }
      />

      {/* ----- Vendas: propostas e contratos ----- */}
      <Route
        path="/vendas/propostas"
        element={
          <RequireAuth>
            <ProposalsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vendas/propostas/:proposalId"
        element={
          <RequireAuth>
            <ProposalPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vendas/contratos"
        element={
          <RequireAuth>
            <ContractsPage />
          </RequireAuth>
        }
      />

      {/* ----- Sucesso do cliente ----- */}
      <Route
        path="/sucesso/contas"
        element={
          <RequireAuth>
            <AccountsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sucesso/contas/:accountId"
        element={
          <RequireAuth>
            <CustomerAccountPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sucesso/playbooks"
        element={
          <RequireAuth>
            <PlaybooksPage />
          </RequireAuth>
        }
      />
      <Route
        path="/sucesso/onboarding"
        element={
          <RequireAuth>
            <OnboardingTemplatesPage />
          </RequireAuth>
        }
      />

      {/* ----- Areas whose API is built and whose screens are not ----- */}
      <Route
        path="/marketing/formularios"
        element={
          <RequireAuth>
            <FormsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/formularios/:formId"
        element={
          <RequireAuth>
            <FormBuilderPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/paginas"
        element={
          <RequireAuth>
            <PagesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/paginas/:pageId"
        element={
          <RequireAuth>
            <PageBuilderPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/dominios"
        element={
          <RequireAuth>
            <DomainsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/email"
        element={
          <RequireAuth>
            <EmailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/campanhas/nova"
        element={
          <RequireAuth>
            <CampaignWizard />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/campanhas"
        element={
          <RequireAuth>
            <CampaignsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/pedidos"
        element={
          <RequireAuth>
            <OrdersPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/cadencias"
        element={
          <RequireAuth>
            <CadencesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/datas"
        element={
          <RequireAuth>
            <KeyDatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/crm/previsao"
        element={
          <RequireAuth>
            <ForecastPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/whatsapp"
        element={
          <RequireAuth>
            <WhatsAppCampaignsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/sms"
        element={
          <RequireAuth>
            <SmsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/social"
        element={
          <RequireAuth>
            <SocialPage />
          </RequireAuth>
        }
      />
      <Route
        path="/marketing/anuncios"
        element={
          <RequireAuth>
            <AdsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trabalho/projetos"
        element={
          <RequireAuth>
            <ProjectsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trabalho/projetos/:projectId"
        element={
          <RequireAuth>
            <ProjectPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trabalho/tarefas"
        element={
          <RequireAuth>
            <TasksPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trabalho/backlog"
        element={
          <RequireAuth>
            <BacklogPage />
          </RequireAuth>
        }
      />
      <Route
        path="/trabalho/tarefas/:taskId"
        element={
          <RequireAuth>
            <TaskPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agenda"
        element={
          <RequireAuth>
            <CalendarPage />
          </RequireAuth>
        }
      />
      <Route
        path="/chat"
        element={
          <RequireAuth>
            <ChatPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agentes"
        element={
          <RequireAuth>
            <AgentsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agentes/lotes"
        element={
          <RequireAuth>
            <BatchesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/agentes/conhecimento"
        element={
          <RequireAuth>
            <KnowledgePage />
          </RequireAuth>
        }
      />
      <Route
        path="/automacoes"
        element={
          <RequireAuth>
            <AutomationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/automacoes/:automationId"
        element={
          <RequireAuth>
            <AutomationBuilder />
          </RequireAuth>
        }
      />
      <Route
        path="/arquivos"
        element={
          <RequireAuth>
            <FilesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/conta"
        element={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/empresa"
        element={
          <RequireAuth>
            <CompanyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/preferencias"
        element={
          <RequireAuth>
            <PreferencesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/cadastros"
        element={
          <RequireAuth>
            <CataloguesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/equipe"
        element={
          <RequireAuth>
            <TeamPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/sso"
        element={
          <RequireAuth>
            <SsoPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/privacidade"
        element={
          <RequireAuth>
            <PrivacyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/auditoria"
        element={
          <RequireAuth>
            <AuditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/integracoes"
        element={
          <RequireAuth>
            <IntegrationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/uso"
        element={
          <RequireAuth>
            <UsagePage />
          </RequireAuth>
        }
      />
      <Route
        path="/configuracoes/assinatura"
        element={
          <RequireAuth>
            <BillingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/busca"
        element={
          <RequireAuth>
            <SearchPage />
          </RequireAuth>
        }
      />

      <Route
        path="/admin"
        element={
          <RequirePlatformAdmin>
            <AdminPage />
          </RequirePlatformAdmin>
        }
      />
      <Route
        path="/admin/tenants/:tenantId"
        element={
          <RequirePlatformAdmin>
            <TenantPage />
          </RequirePlatformAdmin>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App(): React.JSX.Element {
  /*
   * The first touch unlocks the audio.
   *
   * Browsers refuse to play a sound until somebody has interacted with the page, which is the right
   * rule: a tab that makes noise before you have touched it is hostile. So the audio context is
   * created and resumed on the first click or key press, from the gesture itself — anywhere else
   * and the browser rejects it — and once is enough, so the listeners remove themselves.
   */
  useEffect(() => {
    const unlock = (): void => unlockSounds();

    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  return (
    /*
     * Outermost, because the providers are above the shell's boundary and a throw in either of them
     * — the session, the preferences — blanked the whole page with the reason only in a console
     * nobody has open. Nothing in here uses router or query context, so it can sit above both.
     */
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PreferencesProvider>
              {/* One socket for the tab, under the session so it has a token to connect with. */}
              <RealtimeProvider>
                <AppRoutes />
              </RealtimeProvider>
            </PreferencesProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
